<template>
  <div class="audience-step" data-test-id="audience-step">
    <Typography variant="h-5">Select participants</Typography>

    <Alert
      v-if="showEmptySelectionError"
      variant="error"
      message="Please select at least one group"
    />

    <div class="top-actions">
      <v-text-field
        v-model="debounceSearch"
        label="Search"
        solo
        flat
        dense
        outlined
        hide-details
        prepend-inner-icon="mdi-magnify"
        class="search-field"
        color="brandPrimary"
      />
      <Button
        v-if="permissions.includes('groups:create:group')"
        outlined
        iconLeft="mdi-plus"
        @click="showGroupCreator = true"
      >
        Group
      </Button>
    </div>

    <div class="directory-heading">
      <Typography variant="subtitle-1">Directory</Typography>
      <Typography variant="body-2">
        Note: Programs don't support external users at this time
      </Typography>
    </div>

    <GroupTable
      ref="groupTable"
      class="full-width"
      :selected="selectedGroups"
      :search="search"
      :include-everyone="true"
      @select-item="toggleGroup"
    />

    <v-navigation-drawer v-model="showGroupCreator" temporary fixed right width="500">
      <GroupTypeSelector
        v-if="showGroupCreator"
        source="PROGRAM_WIZARD"
        :version="3"
        :clientId="globalClientId"
        @close="showGroupCreator = false"
        @get-groups="onGroupCreated"
        @push-to-groups="onGroupPushed"
      />
    </v-navigation-drawer>
    <v-overlay :value="showGroupCreator" />
  </div>
</template>

<script>
import { mapState } from "vuex";
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import Alert from "@/components/generic/Alert.vue";
import GroupTable from "@/components/data-table/Groups.vue";
import GroupTypeSelector from "@/components/groups/GroupTypeSelector.vue";
import { debounce } from "@/shared_data/functions";

export default {
  name: "AudienceStep",
  components: {
    Typography,
    Button,
    Alert,
    GroupTable,
    GroupTypeSelector,
  },
  props: {
    selectedGroups: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:selectedGroups"],
  data() {
    return {
      search: null,
      debounceSearch: null,
      showGroupCreator: false,
      // Latched by validate() when the user tries to advance with an empty
      // selection; cleared automatically as soon as a group is added.
      validationTriggered: false,
    };
  },
  computed: {
    ...mapState(["permissions", "globalClientId"]),
    showEmptySelectionError() {
      return this.validationTriggered && this.selectedGroups.length === 0;
    },
  },
  methods: {
    toggleGroup(group) {
      const groups = [...this.selectedGroups];
      const idx = groups.findIndex((g) => g.groupId === group.groupId);
      if (idx === -1) {
        groups.push({ groupId: group.groupId, groupDisplayName: group.groupDisplayName });
      } else if (!group.dontRemove) {
        groups.splice(idx, 1);
      }
      this.$emit("update:selectedGroups", groups);
    },
    validate() {
      const isValid = this.selectedGroups.length > 0;
      this.validationTriggered = !isValid;
      return isValid;
    },
    onGroupCreated(groupId) {
      this.search = undefined;
      this.debounceSearch = undefined;
      this.$nextTick(() => {
        if (this.$refs.groupTable) {
          this.$refs.groupTable.getGroups(true, "group-created", groupId);
        }
      });
    },
    onGroupPushed(group) {
      if (!group) return;
      const exists = this.selectedGroups.find((g) => g.groupId === group.groupId);
      if (!exists) {
        this.toggleGroup(group);
      }
    },
  },
  watch: {
    debounceSearch: debounce(function (newVal) {
      this.search = newVal;
    }, 500),
    selectedGroups: {
      immediate: false,
      handler() {
        if (this.selectedGroups.length === 0) {
          this.validationTriggered = true;
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.audience-step {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.top-actions {
  display: flex;
  align-items: center;
  gap: 22px;
}

.search-field {
  flex: 1 1 0;
  min-width: 0;
}

.directory-heading {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.full-width {
  width: 100%;
}
</style>
